import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Dropdown } from "components/Dropdown";
import { Check as CheckIcon, Close as CloseIcon } from "@mui/icons-material";
import {
  CircularProgress,
  IconButton,
  MenuItem,
  TableCell,
  TableRow,
  TextField,
  Tooltip
} from "@mui/material";
import { isNil } from "lodash";

export const MachineStatusEditManager = ({
  testLabel,
  loading,
  minDate,
  machineStatus,
  machineStatusTypes,
  onSubmit,
  onCancel
}) => {
  const defaultMinDate = new Date(-8640000000000000);
  const [nowDate, setNowDate] = useState(new Date());

  useEffect(() => {
    const intervalId = setInterval(() => {
      setNowDate(new Date());
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  });

  const validationSchema = Yup.object({
    status: Yup.number()
      .label("Status")
      .required("Required"),
    date_actioned: Yup.date()
      .label("Date set")
      .nullable()
      .required("Required")
      .min(
        new Date((minDate || defaultMinDate).getTime() + 1000), // we want to have at least 1 second apart between records
        "Must be later than previous status date set"
      )
      .max(nowDate, "Must be earlier than current date/time"),
    expected_rts: Yup.date()
      .label("Expected RTS")
      .test({
        name: "min",
        exclusive: false,
        params: {},
        message: "Date entered must be after the date set.",
        test: (value, context) =>
          isNil(value) || value >= context.parent.date_actioned
      })
      .nullable()
  });

  const initVal = val => {
    return val || val === 0 ? val : "";
  };

  const initDate = date => {
    return date ? new Date(date) : null;
  };

  const toDateTimePickerFormat = date => {
    if (!date) {
      return "";
    }

    try {
      const val = date.toLocaleString("sv").replace(" ", "T");
      return val.slice(0, -3);
    } catch (error) {
      return "";
    }
  };

  const formik = useFormik({
    initialValues: {
      case: initVal(machineStatus.case),
      machine: initVal(machineStatus.machine),
      status: initVal(machineStatus.status),
      date_actioned: initDate(
        machineStatus.date_actioned || new Date().setSeconds(0, 0)
      ),
      expected_rts: initDate(machineStatus.expected_rts)
    },
    validationSchema,
    onSubmit: values => {
      onSubmit(values);
    }
  });

  const onStatusFieldChange = useCallback(
    value => {
      // When status set as Online(1) remove expectedRTS value
      if (value === 1) {
        formik.setFieldValue("expected_rts", initDate(null));
      }

      formik.setFieldValue("status", value);
    },
    [formik]
  );

  const onDateFieldChange = useCallback(
    (target, value) => {
      if (target === "date_actioned") {
        // If date-time changed, reset expected-RTS value
        formik.setFieldValue("expected_rts", initDate(null));
      }

      // Test the date first
      const d = initDate(value);
      if (d && isNaN(d)) {
        return;
      }

      formik.setFieldValue(target, d);
    },
    [formik]
  );

  const isSaveDisabled = () => {
    if (testLabel === "edit") {
      if (machineStatus.expected_rts === formik.values.expected_rts) {
        return true;
      }

      const msRts = new Date(machineStatus.expected_rts).toISOString();

      if (!msRts) {
        return false; // nothing to compare against
      }

      if (isNaN(formik.values.expected_rts)) {
        return true; // not yet entered a correct date and time
      }

      if (!formik.values.expected_rts) {
        return false; // Nothing to compare against
      }

      try {
        return msRts === formik.values.expected_rts.toISOString();
      } catch (e) {
        return true; // The date is in incomplete or incorrect format
      }
    } else {
      return false;
    }
  };

  // MachineStatus Editor
  return (
    <TableRow hover data-testid={`Machine status ${testLabel} row`}>
      {/* Status Column */}
      {
        <TopAlignedTableCell component="th" scope="row">
          <MachineStatusDropdown
            name="status"
            value={formik.values.status}
            onChange={evt => onStatusFieldChange(evt.target.value)}
            size="small"
            error={formik.touched.status && Boolean(formik.errors.status)}
            disabled={loading || testLabel === "edit"}
            helperText={formik.touched.status && formik.errors.status}
            onBlur={formik.handleBlur}
          >
            {machineStatusTypes?.map((status, index) => (
              <MenuItem key={index} value={status.id}>
                {status.name}
              </MenuItem>
            ))}
          </MachineStatusDropdown>
        </TopAlignedTableCell>
      }

      {/* Date set column */}
      {
        <TopAlignedTableCell>
          <StyledDateTimePicker
            type="datetime-local"
            name="date_actioned"
            value={toDateTimePickerFormat(formik.values.date_actioned)}
            error={
              formik.touched.date_actioned &&
              Boolean(formik.errors.date_actioned)
            }
            helperText={
              formik.touched.date_actioned && formik.errors.date_actioned
            }
            onChange={evt =>
              onDateFieldChange("date_actioned", evt.currentTarget.value)
            }
            onBlur={formik.handleBlur}
            variant="outlined"
            margin="dense"
            fullWidth
            disabled={loading || testLabel === "edit"}
            inputProps={{
              min: toDateTimePickerFormat(minDate),
              max: toDateTimePickerFormat(nowDate),
              onKeyDown: e => {
                e.preventDefault();
              }
            }}
            InputLabelProps={{
              shrink: true
            }}
            size="small"
          />
        </TopAlignedTableCell>
      }

      {/* Expected RTS column */}
      <TopAlignedTableCell>
        <StyledDateTimePicker
          type="datetime-local"
          name="expected_rts"
          value={toDateTimePickerFormat(formik.values.expected_rts)}
          onChange={evt =>
            onDateFieldChange("expected_rts", evt.currentTarget.value)
          }
          onBlur={formik.handleBlur}
          variant="outlined"
          margin="dense"
          fullWidth
          disabled={
            loading ||
            typeof formik.values.status !== "number" ||
            !formik.values?.date_actioned ||
            formik.values.status === 1 // Online does not have RTS
          }
          error={
            formik.touched.expected_rts && Boolean(formik.errors.expected_rts)
          }
          helperText={formik.touched.expected_rts && formik.errors.expected_rts}
          inputProps={{
            min: toDateTimePickerFormat(formik.values.date_actioned),
            onKeyDown: e => {
              e.preventDefault();
            }
          }}
          InputLabelProps={{
            shrink: true
          }}
          size="small"
        />
      </TopAlignedTableCell>

      {/* Case id column (Not used for edit) */}
      <TableCell>
        {machineStatus?.displayId ? machineStatus.displayId : null}
      </TableCell>

      {/* Action column */}
      <TableCell>
        <ActionButtonWrap>
          <Tooltip
            title="Save"
            aria-label="save-machine-status"
            placement="top"
          >
            <span>
              <IconButton
                aria-label="Save button"
                color="primary"
                disabled={loading || isSaveDisabled()}
                onClick={() => formik.handleSubmit()}
                size="large"
              >
                {!loading ? <CheckIcon /> : <CircularProgress size={23} />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title="Cancel"
            aria-label="cancel-edit-status"
            placement="top"
          >
            <span>
              <IconButton
                aria-label="Cancel button"
                disabled={loading}
                onClick={() => onCancel()}
                size="large"
              >
                <CloseIcon />
              </IconButton>
            </span>
          </Tooltip>
        </ActionButtonWrap>
      </TableCell>
    </TableRow>
  );
};

const TopAlignedTableCell = styled(TableCell)`
  vertical-align: top;
`;

const StyledDateTimePicker = styled(TextField)`
  margin: 0;
`;

const ActionButtonWrap = styled("div")`
  display: flex;
`;

const MachineStatusDropdown = styled(Dropdown)`
  min-width: 110px;
  width: 100%;
`;

MachineStatusEditManager.propTypes = {
  testLabel: PropTypes.string,
  loading: PropTypes.bool,
  minDate: PropTypes.instanceOf(Date),
  machineStatus: PropTypes.object,
  machineStatusTypes: PropTypes.array,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func
};
